import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  ButtonSmallSubtleIcon,
  Icon,
  IconSVG,
  MicroButton,
} from "@studio3t/generic-components";
import { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useGuardedNavigate } from "@/components/LeaveGuard";
import type { Service } from "@/types/api";
import { setDragPreviewChip } from "./dragPreviewChip";
import {
  canStageAs,
  countNonEmptyScripts,
  countSavedTests,
  type DropEdge,
  SOURCE_EDITOR_ID,
  type StagedResource,
  type StagedRole,
  TRANSFORM_EDITOR_ID,
  usePipelineStaging,
} from "./pipelineStaging";
import { StageNavPanel } from "./StageNavPanel";
import type { StageTabId } from "./StageTabs";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { CollapseSectionIcon, ExpandSectionIcon } from "./sectionIcons";
import { TestsNavPanel } from "./TestsNavPanel";

const SEMANTIC = designVariables.ui.semantic;
const SPACINGS = designVariables.primitives.spacings;
const ITEM = designVariables.ui.sidebar.list.item;
const DROPZONE = designVariables.ui.drag.dropzone;
// Dropzone colors aren't part of the generic-components theme yet — take the
// light-theme values from design-variables directly (drag/dropzone/*).
const DZ_COLORS = designVariables.theme.light.drag.dropzone;

// Payload attached to a draggable explorer resource row and read back on drop.
// Icon travels as its enum value (a string) so the dropped row can render it;
// provider drives per-zone eligibility (see canStageAs). Schema leaves have
// no provider — the isSchema flag routes them to the SCHEMA drop zone only.
export const EXPLORER_RESOURCE_DRAG_KIND = "explorer-resource";

export interface ExplorerResourceDragData extends Record<string, unknown> {
  kind: typeof EXPLORER_RESOURCE_DRAG_KIND;
  id: string;
  name: string;
  icon: Icon;
  provider?: Service["provider"];
  isSchema?: boolean;
  isTest?: boolean;
}

export function isExplorerResourceDragData(
  data: Record<string, unknown>,
): data is ExplorerResourceDragData {
  return data.kind === EXPLORER_RESOURCE_DRAG_KIND;
}

// Payload of a TRANSFORM row being reordered via its drag handle — distinct
// from explorer drags so the section drop zones ignore it and vice versa.
// Shared with the V2 panel's block reorder (StageNavPanel).
export const TRANSFORM_ROW_DRAG_KIND = "staged-transform-row";

export interface TransformRowDragData extends Record<string, unknown> {
  kind: typeof TRANSFORM_ROW_DRAG_KIND;
  id: string;
}

export function isTransformRowDragData(
  data: Record<string, unknown>,
): data is TransformRowDragData {
  return data.kind === TRANSFORM_ROW_DRAG_KIND;
}

// GenericButton drops props outside its own API, so aria-label/title/type
// never reach the DOM — set them through the node instead (same workaround
// as GenericSelect's missing type attribute).
export function useLabeledButton(label: string) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const btn = ref.current;
    if (!btn) return;
    btn.setAttribute("aria-label", label);
    btn.setAttribute("title", label);
    btn.setAttribute("type", "button");
  }, [label]);
  return ref;
}

// Click + Enter/Space activate row-like divs — same semantics as the
// Explorer's rowActivationProps. Shared with the sink-tab panel.
export function toggleActivationProps(action: () => void) {
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: action,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      action();
    },
  };
}

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

// Trailing 32x28 Button-S group (Figma 13984-103133), flush. Clicks/keys are
// contained so they can't toggle the row selection or header collapse —
// GenericButton's onClick gets no event and its disabled state is
// pointer-events:none (those clicks land on the group), so the containment
// lives here for all of them.
export function RowActionsGroup({ children }: { children: React.ReactNode }) {
  return (
    <RowActions onClick={stopPropagation} onKeyDown={stopPropagation}>
      {children}
    </RowActions>
  );
}

// 20px leading icon wrap; active section headers switch to primary green.
// An errored row swaps its icon for the red error glyph (TTTL-215, Figma
// Bridge_PipelineNav 15496-289715), a warned row for the yellow warning
// glyph (15695-132722). Error wins over warning, and both win over the
// active green, selected rows included.
export function LeadingIcon({
  icon,
  active,
  errored,
  warning,
}: {
  icon: Icon;
  active?: boolean;
  errored?: boolean;
  warning?: boolean;
}) {
  const theme = useTheme();
  const status = errored ? "error" : warning ? "warning" : null;
  return (
    <IconWrap>
      <IconSVG
        icon={
          status === "error"
            ? Icon.Error
            : status === "warning"
              ? Icon.Warning
              : icon
        }
        size={18}
        colors={
          status === "error"
            ? { default: theme.statusError }
            : status === "warning"
              ? { default: theme.statusWarning }
              : active
                ? { default: theme.semanticMainPrimary }
                : theme.sidebarListItemIcon
        }
      />
    </IconWrap>
  );
}

// A row action whose flow hasn't landed yet (run/save/edit/new) — rendered
// disabled with an accessible name.
export function PendingRowButton({
  label,
  icon,
}: {
  label: string;
  icon: Icon;
}) {
  const ref = useLabeledButton(label);
  return <ButtonSmallSubtleIcon ref={ref} icon={icon} disabled />;
}

// A live row action with an accessible name (the sink-tab untitled schema
// row's Save).
// Row actions edit the pipeline, so a view-only studio (editsLocked)
// leaves them out altogether — except the ones flagged viewSafe (the
// Runs: they execute, they don't edit).
export function RowActionButton({
  label,
  icon,
  onClick,
  disabled = false,
  viewSafe = false,
}: {
  label: string;
  icon: Icon;
  onClick: () => void;
  disabled?: boolean;
  /** Stays live in the view-only studio. */
  viewSafe?: boolean;
}) {
  const ref = useLabeledButton(label);
  const { editsLocked } = useStudioPipeline();
  if (editsLocked && !viewSafe) return null;
  return (
    <ButtonSmallSubtleIcon
      ref={ref}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
    />
  );
}

// Every staged row's remove control.
export function RemoveRowButton({
  name,
  onRemove,
  disabled = false,
}: {
  name: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const ref = useLabeledButton(`Remove ${name} from pipeline`);
  const { editsLocked } = useStudioPipeline();
  if (editsLocked) return null;
  return (
    <ButtonSmallSubtleIcon
      ref={ref}
      icon={Icon.Minus}
      onClick={onRemove}
      disabled={disabled}
    />
  );
}

// Figma: Bridge_PipelineNavBar (14175-100697) + Studio frame SCHEMA section
// (14136-101722); role badges per Bridge_Explorer (13123-100623). Sections:
// SOURCE, TRANSFORM, SINK and SCHEMA — Tests and the header +/play actions
// come later. SOURCE is a single slot (a new drop replaces the staged
// source); the other sections take any number, each drop appending
// (duplicates by id ignored). The drop zones stay on screen even when a
// section is collapsed. The row minus button unstages a resource.
//
// With `visibleSection` set (the stage tab bar, Figma 14425-283750), only
// that tab's section renders — SCHEMA has no tab in the design and only
// shows in the unfiltered bar.
const selectionIn = (items: StagedResource[], selectedId: string | null) =>
  items.some((r) => r.id === selectedId);

function SourceNavSection() {
  const { sources, stage, effectiveSelectedId, toggleSelected, deselectIf } =
    usePipelineStaging();
  return (
    <NavBarSection
      stageRole="source"
      title="SOURCE"
      icon={Icon.Input}
      items={sources}
      active={
        selectionIn(sources, effectiveSelectedId) ||
        effectiveSelectedId === SOURCE_EDITOR_ID
      }
      selectedId={effectiveSelectedId}
      onToggleSelected={toggleSelected}
      onDrop={(resource) => {
        stage("source", resource);
        // The staged item takes the single SOURCE slot and the default
        // selection with it — an editor-row selection is stale now.
        deselectIf(SOURCE_EDITOR_ID);
      }}
      newItemLabel="Drag from the explorer or use the editor"
      // Always clickable — with a source staged it still starts a fresh
      // editor cycle (placeholder view).
      newItemId={SOURCE_EDITOR_ID}
    />
  );
}

function TransformNavSection() {
  const {
    transforms,
    scriptDrafts,
    stage,
    effectiveSelectedId,
    toggleSelected,
  } = usePipelineStaging();
  return (
    <NavBarSection
      stageRole="transform"
      title="TRANSFORM"
      icon={Icon.Transform}
      items={transforms}
      // An EMPTY untitled script is not a script yet — same rule as the
      // Transform stage tab's badge.
      countOverride={countNonEmptyScripts(transforms, scriptDrafts)}
      active={
        selectionIn(transforms, effectiveSelectedId) ||
        effectiveSelectedId === TRANSFORM_EDITOR_ID
      }
      selectedId={effectiveSelectedId}
      onToggleSelected={toggleSelected}
      onDrop={(resource) => stage("transform", resource)}
      newItemLabel="Drag a script or use the editor"
      newItemId={TRANSFORM_EDITOR_ID}
    />
  );
}

// The classic SINK section of the unfiltered bar; the sink tab swaps in the
// Bridge_PipelineNavV2 layout (StageNavPanel) instead.
function ClassicSinkSection() {
  const { sinks, stage, effectiveSelectedId, toggleSelected } =
    usePipelineStaging();
  const navigate = useGuardedNavigate();
  return (
    <NavBarSection
      stageRole="sink"
      title="SINK"
      icon={Icon.Output}
      items={sinks}
      active={selectionIn(sinks, effectiveSelectedId)}
      selectedId={effectiveSelectedId}
      onToggleSelected={toggleSelected}
      onDrop={(resource) => stage("sink", resource)}
      newItemLabel="New sink"
      onNewItem={() => navigate("/services/new")}
    />
  );
}

export function PipelineNavBar({
  visibleSection,
}: {
  visibleSection?: StageTabId;
}) {
  const { tests, effectiveSelectedId, toggleSelected } = usePipelineStaging();

  return (
    <NavBar data-testid="pipeline-nav-bar">
      {!visibleSection && (
        <>
          <SourceNavSection />
          <SectionSeparator />
          <TransformNavSection />
          <SectionSeparator />
        </>
      )}
      {/* The source, transform and sink tabs got their own
          Bridge_PipelineNavV2 layout (Figma 14397-191575 / MW variant
          14419-217564 / SRC variant 14397-174654): per-item groups with
          nested SCHEMA sub-sections and drag-revealed drop zones. The
          classic sections stay for the unfiltered bar. */}
      {visibleSection === "source" && <StageNavPanel stage="source" />}
      {visibleSection === "transform" && <StageNavPanel stage="transform" />}
      {visibleSection === "sink" && <StageNavPanel stage="sink" />}
      {visibleSection === "tests" && <TestsNavPanel />}
      {!visibleSection && (
        <>
          <ClassicSinkSection />
          <SectionSeparator />
          <TestsSection
            active={selectionIn(tests, effectiveSelectedId)}
            selectedId={effectiveSelectedId}
            onToggleSelected={toggleSelected}
          />
          {/* The design closes the last section with a trailing separator. */}
          <SectionSeparator />
        </>
      )}
    </NavBar>
  );
}

// Shared section chrome: 40px header row with icon, title, count badge and
// the trailing collapse control. An empty section has nothing to expand: the
// collapse control disappears and the header is inert.
function SectionHeaderBar({
  title,
  icon,
  count,
  active,
  expandable,
  collapsed,
  onToggle,
  actions,
}: {
  title: string;
  icon: Icon;
  /** Counter badge value; omit to render no badge (e.g. the SOURCE section). */
  count?: number;
  active: boolean;
  expandable: boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** Trailing header buttons (Figma section/item actions) — clicks must not
   *  toggle the collapse, so they render inside a propagation stop. */
  actions?: React.ReactNode;
}) {
  const headerActivation = expandable
    ? {
        ...toggleActivationProps(onToggle),
        title: collapsed ? "Expand section" : "Collapse section",
      }
    : {};

  return (
    <SectionHeader $interactive={expandable} {...headerActivation}>
      <LeadingIcon icon={icon} active={active} />
      <SectionTitle $active={active}>{title}</SectionTitle>
      {/* Same counter badge as the Explorer sections / Services table. */}
      {count !== undefined && (
        <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>
      )}
      <HeaderSpring />
      {actions && <RowActionsGroup>{actions}</RowActionsGroup>}
      {expandable && <CollapseIndicator collapsed={collapsed} />}
      {/* Green accent only while the selected item is actually visible —
          collapsing the section hides it. */}
      {active && !collapsed && <ActiveBar />}
    </SectionHeader>
  );
}

// Decorative state indicator (as in the Explorer headers) — the whole
// header is the control.
function CollapseIndicator({ collapsed }: { collapsed: boolean }) {
  return (
    <SectionMicroButton
      tabIndex={-1}
      aria-hidden
      icon={collapsed ? <ExpandSectionIcon /> : <CollapseSectionIcon />}
    />
  );
}

function NavBarSection({
  stageRole,
  title,
  icon,
  items,
  active,
  selectedId,
  onToggleSelected,
  onDrop,
  newItemLabel,
  newItemId,
  onNewItem,
  countOverride,
}: {
  stageRole: StagedRole;
  title: string;
  icon: Icon;
  items: StagedResource[];
  /** Replaces the header badge's items.length (e.g. TRANSFORM skipping
   *  empty untitled drafts). */
  countOverride?: number;
  active: boolean;
  selectedId: string | null;
  onToggleSelected: (id: string) => void;
  onDrop: (resource: StagedResource) => void;
  /** "New …" creation row rendered above the drop zone (Figma 13970-92875
   *  pattern); without onNewItem it's a placeholder like New schema. */
  newItemLabel?: string;
  /** When set, the row is a selectable pseudo-item (the SOURCE editor row)
   *  participating in the shared selection instead of a creation action. */
  newItemId?: string;
  onNewItem?: () => void;
}) {
  const { unstage } = usePipelineStaging();
  // Collapsing hides only the staged items — the drop zone stays on screen so
  // a resource can be added at any time.
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Section data-testid={`pipeline-section-${stageRole}`}>
      <SectionHeaderBar
        title={title}
        icon={icon}
        // SOURCE holds at most one item, so it gets no counter badge.
        count={
          stageRole === "source" ? undefined : (countOverride ?? items.length)
        }
        active={active}
        expandable={items.length > 0}
        collapsed={collapsed}
        onToggle={() => setCollapsed((prev) => !prev)}
      />

      {!collapsed &&
        items.map((resource) => (
          <StagedRowItem
            key={resource.id}
            resource={resource}
            stageRole={stageRole}
            // Only TRANSFORM rows are sortable (Figma: the drag handle slot is
            // hidden on source/sink rows) — middleware order matters.
            sortable={stageRole === "transform"}
            selected={selectedId === resource.id}
            onToggleSelected={onToggleSelected}
            onUnstage={unstage}
          />
        ))}

      {newItemLabel && (
        <NewItemRow
          type="button"
          data-testid={`new-${stageRole}-row`}
          // Placeholder rows without a flow are disabled instead of
          // rendering as focusable buttons that do nothing.
          disabled={!onNewItem && !newItemId}
          $selected={newItemId !== undefined && selectedId === newItemId}
          onClick={newItemId ? () => onToggleSelected(newItemId) : onNewItem}
        >
          {newItemLabel}
        </NewItemRow>
      )}

      <SectionDropZone
        testId={`${stageRole}-drop-zone`}
        canDrop={(data) => canStageAs(stageRole, data.provider)}
        onDrop={({ id, name, icon: leafIcon, provider }) =>
          onDrop({ id, name, icon: leafIcon, provider })
        }
      />
    </Section>
  );
}

// Figma TESTS section — empty state 14064-84074, populated 14064-83590.
// Header carries a New-test (+) action and a Run-all button; test rows carry
// run/edit/remove. Run and edit stay disabled until their flows land; the +
// is a placeholder like the topbar actions.
function TestsSection({
  active,
  selectedId,
  onToggleSelected,
}: {
  active: boolean;
  selectedId: string | null;
  onToggleSelected: (id: string) => void;
}) {
  const { tests, stageTest, unstage } = usePipelineStaging();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Section data-testid="pipeline-section-tests">
      <SectionHeaderBar
        title="TESTS"
        icon={Icon.Tests}
        // Only FINAL tests count — saved with content and a proper name
        // (same rule as the Tests stage tab and the suite header badge).
        count={countSavedTests(tests)}
        active={active}
        expandable={tests.length > 0}
        collapsed={collapsed}
        onToggle={() => setCollapsed((prev) => !prev)}
        actions={
          <>
            <PendingRowButton label="New test" icon={Icon.Plus} />
            <PendingRowButton label="Run all tests" icon={Icon.Run} />
          </>
        }
      />

      {!collapsed &&
        tests.map((test) => (
          <TestRowItem
            key={test.id}
            test={test}
            selected={selectedId === test.id}
            onToggleSelected={onToggleSelected}
            onUnstage={unstage}
          />
        ))}

      <SectionDropZone
        testId="tests-drop-zone"
        canDrop={(data) => data.isTest === true}
        onDrop={({ id, name, icon: leafIcon }) =>
          stageTest({ id, name, icon: leafIcon })
        }
      />
    </Section>
  );
}

// Selection-aware wrapper shared by the rows without reorder DnD
// (schema/test rows).
function SelectableRow({
  testId,
  id,
  selected,
  onToggle,
  children,
}: {
  testId: string;
  id: string;
  selected: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <SchemaRow
      data-testid={testId}
      data-resource-id={id}
      $selected={selected}
      {...toggleActivationProps(() => onToggle(id))}
    >
      {children}
    </SchemaRow>
  );
}

// Figma Test row (14064-84469): status slot + test icon + name, trailed by
// run/edit (disabled for now) and remove. The 20px status slot mirrors the
// design's List/item type slot and keeps the icon at indent1.
function TestRowItem({
  test,
  selected,
  onToggleSelected,
  onUnstage,
}: {
  test: StagedResource;
  selected: boolean;
  onToggleSelected: (id: string) => void;
  onUnstage: (id: string) => void;
}) {
  return (
    <SelectableRow
      testId="pipeline-test-item"
      id={test.id}
      selected={selected}
      onToggle={onToggleSelected}
    >
      <RowBody>
        <TypeSlot aria-hidden />
        <LeadingIcon icon={test.icon} />
        <StagedName>{test.name}</StagedName>
      </RowBody>
      <RowActionsGroup>
        <PendingRowButton label={`Run ${test.name}`} icon={Icon.Run} />
        <PendingRowButton label={`Edit ${test.name}`} icon={Icon.Edit} />
        <RemoveRowButton name={test.name} onRemove={() => onUnstage(test.id)} />
      </RowActionsGroup>
    </SelectableRow>
  );
}

// Pointer above the row's midline inserts before it, below inserts after.
export function edgeFromPointer(row: HTMLElement, clientY: number): DropEdge {
  const rect = row.getBoundingClientRect();
  return clientY < rect.top + rect.height / 2 ? "top" : "bottom";
}

// A drag that reorders TRANSFORM rows, excluding drops onto the row itself.
function isReorderDrag(data: Record<string, unknown>, selfId: string) {
  return isTransformRowDragData(data) && data.id !== selfId;
}

// Reorder DnD for sortable TRANSFORM rows: the whole row travels but only
// the handle starts a drag (plain clicks keep toggling selection). While a
// compatible drag hovers the row, dropEdge reports which half the pointer is
// in — that drives the green insertion line.
function useTransformReorderDnd(
  rowRef: React.RefObject<HTMLDivElement | null>,
  handleRef: React.RefObject<HTMLSpanElement | null>,
  resource: StagedResource,
  sortable: boolean,
) {
  const theme = useTheme();
  const { reorderTransforms } = usePipelineStaging();
  const [dragging, setDragging] = useState(false);
  const [dropEdge, setDropEdge] = useState<DropEdge | null>(null);
  const { id, name } = resource;

  useEffect(() => {
    if (!sortable) return;
    const row = rowRef.current;
    const handle = handleRef.current;
    if (!row) return;
    if (!handle) return;
    return combine(
      draggable({
        element: row,
        dragHandle: handle,
        getInitialData: (): TransformRowDragData => ({
          kind: TRANSFORM_ROW_DRAG_KIND,
          id,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) =>
          setDragPreviewChip({ nativeSetDragImage, theme, name }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
      }),
      dropTargetForElements({
        element: row,
        canDrop: ({ source }) => isReorderDrag(source.data, id),
        onDrag: ({ location }) =>
          setDropEdge(edgeFromPointer(row, location.current.input.clientY)),
        onDragLeave: () => setDropEdge(null),
        onDrop: ({ source, location }) => {
          setDropEdge(null);
          if (!isReorderDrag(source.data, id)) return;
          reorderTransforms(
            source.data.id as string,
            id,
            edgeFromPointer(row, location.current.input.clientY),
          );
        },
      }),
    );
  }, [sortable, id, name, reorderTransforms, theme, rowRef, handleRef]);

  return { dragging, dropEdge };
}

function StagedRowItem({
  resource,
  stageRole,
  sortable,
  selected,
  onToggleSelected,
  onUnstage,
}: {
  resource: StagedResource;
  stageRole: StagedRole;
  sortable: boolean;
  selected: boolean;
  onToggleSelected: (id: string) => void;
  onUnstage: (id: string) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  const { dragging, dropEdge } = useTransformReorderDnd(
    rowRef,
    handleRef,
    resource,
    sortable,
  );
  const { id, name } = resource;

  return (
    <StagedRow
      ref={rowRef}
      data-testid={`pipeline-${stageRole}-item`}
      data-resource-id={id}
      $selected={selected}
      $dragging={dragging}
      $dropEdge={dropEdge}
      {...toggleActivationProps(() => onToggleSelected(id))}
    >
      <RowBody>
        {/* Figma List/item Sortable slot: kept on every row for alignment,
            the three-bar handle only shows (and drags) on sortable rows. */}
        <HandleSlot ref={handleRef} $sortable={sortable} aria-hidden>
          {sortable && (
            <>
              <HandleBar />
              <HandleBar />
              <HandleBar />
            </>
          )}
        </HandleSlot>
        <LeadingIcon icon={resource.icon} />
        <StagedName>{name}</StagedName>
      </RowBody>
      <RowActionsGroup>
        {/* Run + save on transform rows only, disabled until their actions
            land; remove on every row. */}
        {stageRole === "transform" && (
          <>
            <PendingRowButton label={`Run ${name}`} icon={Icon.Run} />
            <PendingRowButton label={`Save ${name}`} icon={Icon.Save} />
          </>
        )}
        <RemoveRowButton name={name} onRemove={() => onUnstage(id)} />
      </RowActionsGroup>
    </StagedRow>
  );
}

// The bare dashed drop target — shared between the always-visible section
// zones (wrapped in DropZoneMargin below) and the sink-tab inline zones that
// appear only while a compatible drag is running. `shownDuringDrag` renders
// the zone on the drag/dropzone/active tokens (white + green dashed, Figma
// 14397-193654) instead of the resting grays.
export function ExplorerDropZone({
  testId,
  canDrop,
  onDrop,
  shownDuringDrag = false,
}: {
  testId: string;
  canDrop: (data: ExplorerResourceDragData) => boolean;
  onDrop: (data: ExplorerResourceDragData) => void;
  shownDuringDrag?: boolean;
}) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  // A view-only studio (editsLocked) takes no drop anywhere.
  const { editsLocked } = useStudioPipeline();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) =>
        !editsLocked &&
        isExplorerResourceDragData(source.data) &&
        canDrop(source.data),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false);
        if (!isExplorerResourceDragData(source.data)) return;
        onDrop(source.data);
      },
    });
  }, [canDrop, onDrop, editsLocked]);

  return (
    <DropZone
      ref={ref}
      $active={isDraggedOver}
      $armed={shownDuringDrag}
      data-testid={testId}
    >
      <DropZoneWrap>
        {/* The DropZone component's active/focus variants both draw the Plus
            in mainprimary (Figma 14397-193654 / -193674). */}
        <IconSVG
          icon={Icon.Plus}
          size={16}
          colors={{
            default:
              isDraggedOver || shownDuringDrag
                ? theme.semanticMainPrimary
                : theme.semanticTextLight,
          }}
        />
        <DropZoneText $active={isDraggedOver} $armed={shownDuringDrag}>
          Drop here
        </DropZoneText>
      </DropZoneWrap>
    </DropZone>
  );
}

function SectionDropZone(props: {
  testId: string;
  canDrop: (data: ExplorerResourceDragData) => boolean;
  onDrop: (data: ExplorerResourceDragData) => void;
}) {
  return (
    <DropZoneMargin>
      <ExplorerDropZone {...props} />
    </DropZoneMargin>
  );
}

const NavBar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(p) => p.theme.cards1Background};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

// Figma Bridge_PipelineNavBar/separator (13970-54402): 1px borderdark
// hairline dashed 5px-on/5px-off (SVG stroke-dasharray "5 5"). CSS dashed
// borders can't control the dash length, so the pattern is painted with a
// repeating gradient.
export const SectionSeparator = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: repeating-linear-gradient(
    to right,
    ${(p) => p.theme.semanticBorderDark} 0 5px,
    transparent 5px 10px
  );
`;

// Figma: section/item — 40px row, 2px mainprimary accent flush left marks the
// active section.
const SectionHeader = styled.div<{ $interactive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: 40px;
  flex-shrink: 0;
  padding: 0 ${ITEM.paddingw}px;
  cursor: ${(p) => (p.$interactive ? "pointer" : "default")};

  &:hover {
    background: ${(p) =>
      p.$interactive ? p.theme.sidebarListItemHoverBackground : "transparent"};
  }
`;

const ActiveBar = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  width: 2px;
  height: 40px;
  background: ${(p) => p.theme.semanticMainPrimary};
`;

// Pushes the header's trailing collapse control to the right edge.
const HeaderSpring = styled.span`
  flex: 1;
`;

const SectionMicroButton = styled(MicroButton)`
  color: ${(p) => p.theme.sidebarListItemText};
`;

// Figma List/item trailing instance — the row's remove control. The row
// itself keeps the default cursor; only this control reads as clickable.
// Figma trailing group (13984-103133): 32x28 buttons sit flush, no gap —
// the row's 10px gap separates the group from the List/item body.
const RowActions = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

// Figma: the active section title uses textheading, inactive ones textlight.
const SectionTitle = styled.span<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) =>
    p.$active ? p.theme.semanticTextHeading : p.theme.semanticTextLight};
`;

// Figma List/item Sortable slot (14px wide) — leading on every staged row so
// names align whether or not the row can be reordered; the bars render only
// on sortable rows. Shared with the V2 panel's block reorder.
export const HandleSlot = styled.span<{ $sortable: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 14px;
  height: 20px;
  flex-shrink: 0;
  cursor: ${(p) => (p.$sortable ? "grab" : "default")};
`;

export const HandleBar = styled.span`
  display: block;
  width: 6px;
  height: 1.5px;
  flex-shrink: 0;
  background: ${(p) => p.theme.sidebarListItemIcon.default};
`;

// Figma 13984-103119: a staged row is a wrapper around a List/item body and
// the trailing button group — spacings/m (6px) left inset, semantic/spacings
// (10px) right inset and body↔buttons gap; the body keeps its own paddingw.
// 6 + paddingw + 14px handle slot + 6px gap lands the leading icon at
// indent1 (38px), aligning every row with the design's sublevel items. The
// green active background marks the clicked (selected) row only; unselected
// rows just show hover feedback. While a reorder drag hovers the row, a 2px
// mainprimary line marks the insertion edge; the dragged row itself dims.
const StagedRow = styled.div<{
  $selected: boolean;
  $dragging: boolean;
  $dropEdge: DropEdge | null;
}>`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  height: 40px;
  flex-shrink: 0;
  padding-left: ${SPACINGS.m}px;
  padding-right: ${SEMANTIC.spacings}px;
  cursor: default;
  opacity: ${(p) => (p.$dragging ? 0.5 : 1)};
  box-shadow: ${(p) =>
    p.$dropEdge == null
      ? "none"
      : p.$dropEdge === "top"
        ? `inset 0 2px 0 0 ${p.theme.semanticMainPrimary}`
        : `inset 0 -2px 0 0 ${p.theme.semanticMainPrimary}`};
  background: ${(p) =>
    p.$selected ? p.theme.sidebarListItemActiveBackground : "transparent"};

  &:hover {
    background: ${(p) =>
      p.$selected
        ? p.theme.sidebarListItemActiveBackground
        : p.theme.sidebarListItemHoverBackground};
  }
`;

// Figma Studio-frame Schema row (14136-101744): same wrapper recipe as
// StagedRow but with no left inset — the body's paddingw alone indents the
// name (schema rows have no handle slot or leading icon to align).
const SchemaRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  height: 40px;
  flex-shrink: 0;
  padding-right: ${SEMANTIC.spacings}px;
  cursor: default;
  background: ${(p) =>
    p.$selected ? p.theme.sidebarListItemActiveBackground : "transparent"};

  &:hover {
    background: ${(p) =>
      p.$selected
        ? p.theme.sidebarListItemActiveBackground
        : p.theme.sidebarListItemHoverBackground};
  }
`;

// Figma 13970-92875 List/item "New schema" (reused for New source/transform/
// sink): 40px row indented to indent1, Medium-italic name. Hover matches the
// other nav bar rows — full-width square highlight, no pill.
const NewItemRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  flex-shrink: 0;
  padding: 0 ${ITEM.paddingw}px 0 ${ITEM.indent1}px;
  border: none;
  background: ${(p) =>
    p.$selected ? p.theme.sidebarListItemActiveBackground : "transparent"};
  font-size: 14px;
  font-weight: 500;
  font-style: italic;
  line-height: 1.2;
  text-align: left;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  cursor: ${(p) => (p.$selected ? "default" : "pointer")};

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$selected
        ? p.theme.sidebarListItemActiveBackground
        : p.theme.sidebarListItemHoverBackground};
  }

  &:disabled {
    cursor: default;
  }
`;

// Figma Test row List/item type slot — 20px placeholder that will hold the
// test-status glyph once results exist; keeps the leading icon at indent1.
const TypeSlot = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

// The List/item part of a staged row (Figma 13984-103132): handle slot,
// leading icon and name, with the item's own horizontal padding.
const RowBody = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: 40px;
  padding-left: ${ITEM.paddingw}px;
  padding-right: ${ITEM.paddingw}px;
`;

const StagedName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.semanticTextBody};
`;

// Figma uses semantic/spacingxs (5px) for the horizontal inset; the token
// isn't exported by @studio3t/design-variables yet, so the value is inlined.
const SPACING_XS = 5;

const DropZoneMargin = styled.div`
  padding: ${SEMANTIC.spacings}px ${SPACING_XS}px;
  flex-shrink: 0;
`;

// Figma DropZone (13970-83568): 41px dashed box; dragging a compatible item
// over it switches to the focus tokens (green border, tinted background).
// Armed zones (sink tab, shown only mid-drag) rest on the active tokens —
// white with the green dashed border (14397-193654).
const DropZone = styled.div<{ $active: boolean; $armed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${DROPZONE.gap}px;
  height: 41px;
  flex: 1;
  min-width: 0;
  padding: ${DROPZONE.padding}px;
  border: 1px dashed
    ${(p) =>
      p.$active
        ? DZ_COLORS.focus.border
        : p.$armed
          ? DZ_COLORS.active.border
          : DZ_COLORS.default.border};
  border-radius: ${DROPZONE.corner}px;
  background: ${(p) =>
    p.$active
      ? DZ_COLORS.focus.background
      : p.$armed
        ? DZ_COLORS.active.background
        : DZ_COLORS.default.background};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
`;

const DropZoneWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${DROPZONE.gap}px;
  flex: 1;
  min-width: 0;
  padding: ${DROPZONE.padding}px;
`;

const DropZoneText = styled.span<{ $active: boolean; $armed?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) =>
    p.$active
      ? DZ_COLORS.focus.text
      : p.$armed
        ? DZ_COLORS.active.text
        : DZ_COLORS.default.text};
`;
